import { createFileRoute } from '@tanstack/react-router'

import type { GiftCard } from '@/api/admin-gift-card-types'
import { useUpdateGiftCard } from '@/api/api-mutations'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/_general/gift-cards/$giftCardUuid/edit')({
	component: RouteComponent,
	staticData: {
		crumb: ({ context: { L } }) => L.GiftCards.EditTitle()
	}
})

function RouteComponent() {
	const { giftCardUuid } = Route.useParams()
	return (
		<RouteModal asDrawer requiresData loader={async api => await api.getGiftCard({ path: { giftCardUuid } })}>
			{data => <EditGiftCardForm card={data} />}
		</RouteModal>
	)
}

function EditGiftCardForm({ card }: { card: GiftCard }) {
	const { L } = useI18n()
	const { handleSuccess } = useRouteModal()
	const { giftCardUuid } = Route.useParams()

	const updateCard = useUpdateGiftCard()

	const form = useAppForm({
		defaultValues: {
			name: card.name
		},
		onSubmit: async ({ value }) => {
			const result = await updateCard({ path: { giftCardUuid }, body: { name: value.name } })
			if (result.isSuccess) {
				toast.success(L.GiftCards.EditSuccess())
				handleSuccess()
			} else {
				toast.error(L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.GiftCards.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => <field.TextField autoFocus label={L.Fields.Name()} />}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
